import { useCallback, useContext, useEffect, useState } from 'react';
import { FormStateContext, UserInitializationContext } from '|/context';
import { currency } from '|/helpers';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { useRefreshDashboardSummary } from '|/hooks/use-dashboard-summary-refresh';
import { useNativePlaid } from './plaid-modal';
import { isDigitalWalletSelection, isPayLaterSelection } from './utils/digital-wallet';
import { usePaymentContext } from '../../contexts/payment';
import { collectPayment, fetchPaymentSummary, submitConsents } from '../../services';
import type { GetPaymentSummaryResponse } from '../../services/purchase-payments-api.types';

export const getPayAtOrderButtonText = (amountDue: number, isPayLater: boolean) =>
  amountDue > 0 && !isPayLater
    ? `Pay ${currency(amountDue).replace('.00', '')} now`
    : 'Place order';

export const getPayAtOrderConsentTypes = (
  consents: GetPaymentSummaryResponse['consents'],
  shouldSubmitConsents: boolean
) =>
  shouldSubmitConsents
    ? consents.map(({ consentType, consentSubType }) => ({ consentType, consentSubType }))
    : [];

interface UsePayAtOrderOptions {
  onSubmit: () => Promise<boolean>;
  buttonText?: string;
  useDigitalWalletCheckout?: boolean;
  shouldProcessPayment?: boolean;
  shouldSubmitConsents?: boolean;
}

export const usePayAtOrder = ({
  onSubmit,
  buttonText: buttonTextOverride,
  useDigitalWalletCheckout: useDigitalWalletCheckoutOverride,
  shouldProcessPayment = true,
  shouldSubmitConsents = true
}: UsePayAtOrderOptions) => {
  const {
    state: { paymentSummary },
    dispatch: paymentDispatch
  } = usePaymentContext();
  const {
    shouldDisableWhileImpersonating,
    shouldInitiateRequestForPayment,
    shouldCollectPayment,
    requestForPaymentAmount,
    isCardEligible,
    purchasePaymentBreakdown,
    paymentSelection,
    consents
  } = paymentSummary;

  const { accessToken } = useContext(UserInitializationContext);
  const { trackEvent } = useAnalyticsContext();
  const { setSubmitError } = useContext(FormStateContext);
  const refreshDashboardSummary = useRefreshDashboardSummary();

  const [hasCustomerConfirmedSufficientFunds, setHasCustomerConfirmedSufficientFunds] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDigitalWalletSupported, setIsDigitalWalletSupported] = useState(false);
  const [isPlaidModalOpen, setIsPlaidModalOpen] = useState(false);

  const isUserBeingImpersonated = accessToken?.user.is_impersonating.toLowerCase() === 'true';
  const hasDigitalWalletSelection = isDigitalWalletSelection(paymentSelection);
  const shouldUseDigitalWallet = useDigitalWalletCheckoutOverride ?? hasDigitalWalletSelection;
  const isPayLater = isPayLaterSelection(paymentSelection);
  const isDigitalWalletAvailable = isDigitalWalletSupported && isCardEligible;
  const shouldCollectPaymentOnSubmit = shouldProcessPayment && shouldCollectPayment;
  const shouldInitiateRequestForPaymentOnSubmit =
    shouldProcessPayment && shouldInitiateRequestForPayment;
  const shouldBlockImpersonatedSubmit = shouldDisableWhileImpersonating && !isPayLater;
  const shouldShowConsents = !shouldUseDigitalWallet || isDigitalWalletAvailable;
  const shouldShowImpersonationText =
    isUserBeingImpersonated && (shouldBlockImpersonatedSubmit || shouldUseDigitalWallet);
  const shouldRequireSufficientFunds =
    purchasePaymentBreakdown.amountDue > 0 &&
    paymentSelection?.selectionType === 'BankAccount' &&
    !!paymentSelection.bankAccount?.accountMask;

  useEffect(() => {
    setHasCustomerConfirmedSufficientFunds(!shouldRequireSufficientFunds);
  }, [shouldRequireSufficientFunds]);

  const isButtonDisabled =
    isSubmitting || !hasCustomerConfirmedSufficientFunds || shouldShowImpersonationText;
  const buttonText =
    buttonTextOverride ?? getPayAtOrderButtonText(purchasePaymentBreakdown.amountDue, isPayLater);

  const handleSubmit = useCallback(async () => {
    await onSubmit();
  }, [onSubmit]);

  const handleError = useCallback(() => {
    setSubmitError(new Error('Failed to collect payment'));
  }, [setSubmitError]);

  const onPlaidExit = useCallback(() => {
    /* re-show PAO inline — no-op since it's already visible */
  }, []);

  const { isNativeApp, openNativePlaid } = useNativePlaid({
    onSuccess: handleSubmit,
    onExit: onPlaidExit,
    onError: handleError
  });

  const openPlaidModal = isNativeApp
    ? openNativePlaid
    : () => {
        setIsPlaidModalOpen(true);
      };

  const onCardAdded = useCallback(async () => {
    const summary = await fetchPaymentSummary();
    paymentDispatch({
      type: 'SET_IS_CARD_ELIGIBLE',
      payload: summary.isCardEligible
    });
    return summary;
  }, [paymentDispatch]);

  const submitPayAtOrder = async (): Promise<boolean> => {
    setIsSubmitting(true);

    const consentTypes = getPayAtOrderConsentTypes(consents, shouldSubmitConsents);

    trackEvent('pp_complete_purchase_pao_submit', {
      isUserBeingImpersonated,
      consentTypes,
      shouldInitiateRequestForPayment: shouldInitiateRequestForPaymentOnSubmit,
      shouldCollectPayment: shouldCollectPaymentOnSubmit,
      requestForPaymentAmount,
      purchasePaymentBreakdown,
      paymentSelection,
      buttonText
    });

    try {
      if (isUserBeingImpersonated) {
        if (shouldBlockImpersonatedSubmit) {
          throw new Error('Disabled while impersonating');
        }

        const didNavigate = await onSubmit();
        setIsSubmitting(didNavigate);
        return didNavigate;
      }

      if (shouldCollectPaymentOnSubmit) {
        const { paymentCollectionSucceeded } = await collectPayment(consentTypes);

        if (!paymentCollectionSucceeded) {
          refreshDashboardSummary();
          return true;
        }

        const didNavigate = await onSubmit();
        setIsSubmitting(didNavigate);
        return didNavigate;
      }

      if (consentTypes.length > 0) {
        await submitConsents(consentTypes);
      }

      if (shouldInitiateRequestForPaymentOnSubmit) {
        openPlaidModal();
        setIsSubmitting(false);
        return true;
      }

      const didNavigate = await onSubmit();
      setIsSubmitting(didNavigate);
      return didNavigate;
    } catch {
      setIsSubmitting(false);
      handleError();
      return false;
    }
  };

  return {
    paymentSummary,
    purchasePaymentBreakdown,
    paymentSelection,
    requestForPaymentAmount,
    isCardEligible,
    isSubmitting,
    isButtonDisabled,
    isDigitalWalletAvailable,
    isDigitalWalletSupported,
    isPlaidModalOpen,
    isUserBeingImpersonated,
    shouldUseDigitalWallet,
    hasDigitalWalletSelection,
    shouldShowConsents,
    shouldShowImpersonationText,
    shouldInitiateRequestForPayment: shouldInitiateRequestForPaymentOnSubmit,
    hasCustomerConfirmedSufficientFunds,
    buttonText,
    setHasCustomerConfirmedSufficientFunds,
    setIsDigitalWalletSupported,
    setIsPlaidModalOpen,
    onCardAdded,
    handleError,
    handleSubmit,
    onPlaidExit,
    submitPayAtOrder
  };
};

export type PayAtOrderController = ReturnType<typeof usePayAtOrder>;
